import { Link } from 'react-router-dom';
import { Sparkles, Eye, MessageCircle, Layers } from 'lucide-react';
import { useExperienceMode } from '@/contexts/ExperienceModeContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { getAlternateModeRooms } from '@/data/rooms';

const AlternateModeRoomsSection = () => {
  const { mode, isDiscovered } = useExperienceMode();
  const { t, isRTL } = useLanguage();
  const alternateRooms = getAlternateModeRooms();

  // Only show when in alternate mode and discovered
  if (mode !== 'alternate' || !isDiscovered) return null;

  const iconMap: Record<string, React.ReactNode> = {
    'the-other-side': <Eye className="w-4 h-4" />,
    'the-unraveling': <Layers className="w-4 h-4" />,
    'the-echo': <MessageCircle className="w-4 h-4" />,
  };

  return (
    <section className="px-4 sm:px-6 pb-12">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-xs uppercase tracking-widest text-muted-foreground/40 mb-4 flex items-center gap-2">
          <Sparkles className="w-3 h-3 animate-pulse" />
          <span className="glitch-text">{t('alternate_rooms') || '░▓ P̵a̶r̷a̵l̶l̷e̶l̵ ̶R̷e̵a̸l̶i̶t̵y̷ ▓░'}</span>
        </h2>
        
        <div className="grid sm:grid-cols-3 gap-3">
          {alternateRooms.map((room, index) => (
            <Link 
              key={room.id} 
              to={room.path}
              className="group relative p-6 border border-foreground/10 hover:border-foreground/30 rounded-lg transition-all duration-500 bg-gradient-to-br from-foreground/5 to-transparent overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Glitch overlay */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, hsl(var(--foreground) / 0.02) 2px, hsl(var(--foreground) / 0.02) 4px)',
                  }}
                />
              </div>

              <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="text-muted-foreground/40 group-hover:text-foreground/60 transition-colors">
                  {iconMap[room.id] || <Sparkles className="w-4 h-4" />}
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                  <h3 className="font-light text-sm group-hover:text-foreground transition-colors">
                    {room.title}
                  </h3>
                  <p className="text-muted-foreground/40 text-xs mt-1 font-mono">
                    {room.subtitle}
                  </p>
                </div>
              </div>

              {/* Floating particles on hover */}
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-foreground/20 rounded-full animate-float"
                    style={{
                      left: `${20 + i * 30}%`,
                      top: `${30 + i * 20}%`,
                      animationDelay: `${i * 0.3}s`,
                    }}
                  />
                ))}
              </div>
            </Link>
          ))}
        </div>

        <p className="text-center text-[10px] text-muted-foreground/20 mt-4 font-mono">
          {t('alternate_rooms_hint') || '[ these rooms exist only in the parallel ]'}
        </p>
      </div>
    </section>
  );
};

export default AlternateModeRoomsSection;
